import React, { useState, useEffect } from "react";
import { Layout, Table, Button, Space, Select, Input, InputNumber, Row, Col, Tag, message, Descriptions, Card, Popconfirm } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import { PlusOutlined, SaveOutlined, SendOutlined, DeleteOutlined, FileTextOutlined, InfoCircleOutlined } from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import "../../../styles/fluidDesign.css";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";
import { getStatusColor } from "../../../utils/statusHelper";

const { Content } = Layout;
const { Option } = Select;

const ManageRequestItems = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [mrMaster, setMrMaster] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [requestItems, setRequestItems] = useState([]);

  // Add Item Fields
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [itemReason, setItemReason] = useState("");
  const [currentItemStock, setCurrentItemStock] = useState(0);
  const [fetchingStock, setFetchingStock] = useState(false);

  useEffect(() => {
    fetchMRData();
    fetchInventoryItems();
  }, [id]);

  const fetchMRData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/inventory/material_request/get.php?mr_id=${id}`);
      if (res.data && res.data.status === "success") {
        const dataset = res.data.dataset;
        setMrMaster(dataset);
        
        const lines = (dataset.items || []).map(item => ({
          in_id: Number(item.in_id),
          in_code: item.in_code,
          in_name: item.in_name,
          uom_name: item.uom_name,
          request_qty: parseFloat(item.request_qty),
          reason_for_used: item.reason_for_used || ""
        }));
        
        setRequestItems(lines);
      } else {
        message.error("Failed to load Material Request details.");
      }
    } catch (error) {
      message.error("Error loading Material Request.");
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const res = await api.get("/inventory/item/list.php");
      if (res.data && res.data.status === "success") {
        setInventoryItems(res.data.dataset || []);
      }
    } catch (error) {
      message.error("Failed to load inventory items.");
    }
  };

  const handleItemSelect = async (itemId) => {
    setSelectedItemId(itemId);
    const selectedItem = inventoryItems.find(ii => Number(ii.in_id) === Number(itemId));
    if (selectedItem) {
      setQuantity(1.00);
      try {
        setFetchingStock(true);
        const res = await api.get(`/inventory/item/get_warehouse_stock.php?in_id=${itemId}`);
        if (res.data && res.data.status === "success") {
          setCurrentItemStock(parseFloat(res.data.location_stock) || 0);
        } else {
          setCurrentItemStock(0);
        }
      } catch (error) {
        setCurrentItemStock(0);
      } finally {
        setFetchingStock(false);
      }
    } else {
      setCurrentItemStock(0);
    }
  };

  const syncMRItemsToDB = async (updatedItems) => {
    try {
      const payload = {
        mr_id: parseInt(id),
        required_date: mrMaster.required_date,
        mr_reason: mrMaster.mr_reason || mrMaster.reason_for_used || "",
        mr_status: mrMaster.req_status || "Draft",
        items: updatedItems
      };
      const res = await api.post("/inventory/material_request/update.php", payload);
      if (res.data && res.data.status === "success") {
        await fetchMRData();
      } else {
        message.error(res.data.message || "Failed to sync items to database.");
        await fetchMRData();
      }
    } catch (error) {
      message.error("Failed to sync items to database.");
      await fetchMRData();
    }
  };

  const handleAddItem = () => {
    if (!selectedItemId) {
      message.warning("Please select an item.");
      return;
    }
    const parsedQty = parseFloat(quantity);
    if (isNaN(parsedQty) || parsedQty <= 0) {
      message.warning("Quantity must be greater than 0.");
      return;
    }

    const itemExists = requestItems.some(item => Number(item.in_id) === Number(selectedItemId));
    if (itemExists) {
      message.warning("This item is already added. Please remove it first to re-add with new values.");
      return;
    }

    const selectedItem = inventoryItems.find(ii => Number(ii.in_id) === Number(selectedItemId));

    // Requested quantity cannot exceed the available stock
    if (parsedQty > currentItemStock) {
      const itemLabel = selectedItem ? `${selectedItem.in_code} - ${selectedItem.in_name}` : "this item";
      message.error(
        `Requested quantity (${parsedQty}) for ${itemLabel} exceeds available stock (${currentItemStock}). Only ${currentItemStock} available.`
      );
      return;
    }

    const newItem = {
      in_id: Number(selectedItemId),
      in_code: selectedItem ? selectedItem.in_code : "",
      in_name: selectedItem ? selectedItem.in_name : "",
      uom_name: selectedItem ? selectedItem.uom_name : "",
      request_qty: parsedQty,
      reason_for_used: itemReason.trim()
    };

    const updated = [...requestItems, newItem];
    setRequestItems(updated);
    syncMRItemsToDB(updated);

    // Reset fields
    setSelectedItemId(null);
    setQuantity(1);
    setItemReason("");
    setCurrentItemStock(0);
    message.success("Item added to request list.");
  };

  const handleRemoveItem = (inId) => {
    const updated = requestItems.filter(item => Number(item.in_id) !== Number(inId));
    setRequestItems(updated);
    syncMRItemsToDB(updated);
    message.success("Item removed.");
  };

  // Re-validates every requested quantity against the current available
  // stock before saving/submitting (stock may have changed since add time).
  const validateItemsAgainstStock = async (items) => {
    const uniqueIds = [...new Set(items.map((item) => Number(item.in_id)))];
    for (const inId of uniqueIds) {
      try {
        const res = await api.get(`/inventory/item/get_warehouse_stock.php?in_id=${inId}`);
        const stock = res.data && res.data.status === "success" ? parseFloat(res.data.location_stock) || 0 : 0;
        const requested = items
          .filter((item) => Number(item.in_id) === Number(inId))
          .reduce((sum, item) => sum + (parseFloat(item.request_qty) || 0), 0);
        if (requested > stock) {
          const inv = inventoryItems.find((ii) => Number(ii.in_id) === Number(inId));
          const label = inv ? `${inv.in_code} - ${inv.in_name}` : `item #${inId}`;
          return { valid: false, message: `Requested quantity (${requested}) for ${label} exceeds available stock (${stock}).` };
        }
      } catch (error) {
        // Ignore per-item fetch failures here; the backend re-validates on save.
      }
    }
    return { valid: true, message: "" };
  };

  const handleSave = async (submitStatus) => {
    if (requestItems.length === 0) {
      message.warning("Please add at least one line item.");
      return;
    }

    const hasInvalidQty = requestItems.some(item => parseFloat(item.request_qty || 0) <= 0);
    if (hasInvalidQty) {
      message.warning("All items must have a quantity greater than 0.");
      return;
    }

    const stockCheck = await validateItemsAgainstStock(requestItems);
    if (!stockCheck.valid) {
      message.error(stockCheck.message);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        mr_id: parseInt(id),
        required_date: mrMaster.required_date,
        mr_reason: mrMaster.mr_reason || mrMaster.reason_for_used || "",
        mr_status: submitStatus,
        items: requestItems
      };

      const res = await api.post("/inventory/material_request/update.php", payload);
      if (res.data && res.data.status === "success") {
        message.success(`Material Request successfully saved as ${submitStatus.toLowerCase()}.`);
        navigate("/inventory/material-request/list");
      } else {
        message.error(res.data.message || "Failed to save Material Request.");
      }
    } catch (error) {
      message.error("Failed to save Material Request.");
    } finally {
      setLoading(false);
    }
  };



  const isEditable = mrMaster && (mrMaster.req_status === "Draft" || mrMaster.req_status === "Rejected");

  const selectedItemDetails = selectedItemId ? inventoryItems.find(ii => Number(ii.in_id) === Number(selectedItemId)) : null;

  const itemColumns = [
    {
      title: "Item Code",
      dataIndex: "in_code",
      key: "in_code",
    },
    {
      title: "Item Name",
      dataIndex: "in_name",
      key: "in_name",
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>
    },
    {
      title: "UOM",
      dataIndex: "uom_name",
      key: "uom_name",
      render: (text) => text || "-"
    },
    {
      title: "Requested Qty",
      dataIndex: "request_qty",
      key: "request_qty",
      align: 'right',
      render: (val) => parseFloat(val || 0).toLocaleString()
    },
    {
      title: "Reason for Use",
      dataIndex: "reason_for_used",
      key: "reason_for_used",
      render: (text) => text || "-"
    },
    {
      title: "Action",
      key: "action",
      align: 'center',
      render: (_, record) => (
        isEditable && (
          <Popconfirm
            title="Remove this item?"
            onConfirm={() => handleRemoveItem(record.in_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        )
      )
    }
  ];

  return (
    <Layout className="fluid-page">
      <Headerdiv />
      
      <div className="fluid-page-header">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <AppBreadcrumb items={[
          { label: 'Inventory', link: '/inventory-items' },
          { label: 'Request Items' },
        ]} />
        <h1 className="fluid-page-title">
          <FileTextOutlined style={{ marginRight: 12 }} />
          Request Items: {mrMaster ? mrMaster.mr_ref : "Loading..."}
        </h1>
        
      </div>

      <Content className="fluid-content">
        {mrMaster && (
          <Row gutter={[24, 24]}>
            {/* Request Overview */}
            <Col span={24}>
              <Card className="fluid-card" title="Request Overview">
                <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="small">
                  <Descriptions.Item label="Request Reference"><strong>{mrMaster.mr_ref}</strong></Descriptions.Item>
                  <Descriptions.Item label="Required Date">{dayjs(mrMaster.required_date).format("YYYY-MM-DD")}</Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={getStatusColor(mrMaster.req_status)} style={{ fontWeight: "600" }}>
                      {mrMaster.req_status}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Created By">{mrMaster.creator_name || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Requested Date">{dayjs(mrMaster.req_dt).format("YYYY-MM-DD hh:mm:ss A")}</Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            {/* Add Item Form (Only if Draft/Rejected) */}
            {isEditable && (
              <Col span={24}>
                <Card className="fluid-card" title={<span><PlusOutlined style={{ marginRight: 8 }} />Add Item to Request</span>}>
                  <Row gutter={16} align="bottom" className="fluid-add-item-row">
                    <Col xs={24} md={10}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: 'var(--fluid-text-primary)' }}>Select Inventory Item</label>
                      <Select allowClear
                        showSearch
                        style={{ width: "100%" }}
                        placeholder="Search item by name or code..."
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                          const label = option?.label || option?.children;
                          return String(label || '').toLowerCase().includes(input.toLowerCase());
                        }}
                        value={selectedItemId}
                        onChange={handleItemSelect}
                      >
                        {inventoryItems.map(ii => (
                          <Option key={ii.in_id} value={ii.in_id} label={`${ii.in_code} - ${ii.in_name}`}>
                            {ii.in_code} - {ii.in_name}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} sm={12} md={3}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: 'var(--fluid-text-primary)' }}>Request Quantity</label>
                      <InputNumber
                        min={0.01}
                        style={{ width: '100%' }}
                        value={quantity}
                        onChange={setQuantity}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: 'var(--fluid-text-primary)' }}>Reason for Use</label>
                      <Input
                        placeholder="Purpose / reason for this item..."
                        value={itemReason}
                        onChange={(e) => setItemReason(e.target.value)}
                        className="fluid-input"
                      />
                    </Col>
                    <Col xs={24} md={3}>
                      <Button
                        type="primary"
                        className="fluid-btn-primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddItem}
                        disabled={!selectedItemId || fetchingStock}
                        block
                        style={{ minWidth: 'unset', borderRadius: '6px' }}
                      >
                        Add
                      </Button>
                    </Col>
                  </Row>
                  
                  {/* Item Specifications / Stock status */}
                  {selectedItemDetails && (
                    <div style={{ marginTop: 20 }}>
                      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <InfoCircleOutlined style={{ color: 'var(--fluid-primary)' }} />
                        <span style={{ fontWeight: 600, color: '#262626' }}>Item Specifications</span>
                      </div>
                      <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
                        <Descriptions.Item label="Item Code">
                          {selectedItemDetails.in_code || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Unit of Measure (UOM)">
                          {selectedItemDetails.uom_name || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Available Stock">
                          {fetchingStock ? (
                            <small>Checking...</small>
                          ) : (
                            <strong style={{ color: parseFloat(currentItemStock) > 0 ? '#52c41a' : '#ff4d4f' }}>
                              {parseFloat(currentItemStock).toLocaleString()} {selectedItemDetails.uom_name}
                            </strong>
                          )}
                        </Descriptions.Item>
                      </Descriptions>
                    </div>
                  )}
                </Card>
              </Col>
            )}

            {/* Items List Table */}
            <Col span={24}>
              <Card 
                className="fluid-card" 
                title={`Request Line Items (${requestItems.length})`}
                extra={!isEditable && <Tag color="warning" style={{ fontWeight: 600 }}>READ ONLY MODE</Tag>}
              >
                <Table
                  columns={itemColumns}
                  dataSource={requestItems}
                  rowKey="in_id"
                  pagination={false}
                  bordered
                  size="middle"
                  scroll={{ x: 'max-content' }}
                  style={{ marginBottom: 20 }}
                />

                {isEditable && (
                  <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                    <Space size="middle">
                      <Button
                        type="default"
                        icon={<SaveOutlined />}
                        onClick={() => handleSave("Draft")}
                        loading={loading}
                        style={{ minWidth: '150px' }}
                      >
                        Save as Draft
                      </Button>
                      {requestItems.length > 0 && (
                        <Popconfirm
                          title="Submit this Material Request for approval?"
                          onConfirm={() => handleSave("Pending")}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            type="primary"
                            icon={<SendOutlined />}
                            loading={loading}
                            className="fluid-btn-primary"
                            style={{ minWidth: '150px' }}
                          >
                            Submit Request
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        )}
      </Content>
      <Footerdiv />
    </Layout>
  );
};

export default ManageRequestItems;
